import { useBookScheduleSpeakingClass, useBookScheduleSpeakingClassMutation } from "@/data/book-speaking-class";
import { useMe } from "@/data/user";
import { ScheduleSpeakingClassesEvent } from "@/types";
import dayjs, { Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useEffect, useMemo, useRef, useState } from "react";
import { INTERNAL_DATE_FORMAT } from "../agenda";
import { t } from "i18next";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(advancedFormat);

// const useScheduleSpeakingClass = ({ onConfirm }: { onConfirm: (events: ScheduleSpeakingClassesEvent[] | null) => void; }) => {

// const useScheduleSpeakingClass = ({ onConfirm } : { onConfirm: (d: ScheduleSpeakingClassesEvent[] | null) => void}) => {
const useScheduleSpeakingClass = ({ onConfirm } : { onConfirm: (d: ScheduleSpeakingClassesEvent[] | null) => void}) => {
    const { me } = useMe();
    const currentWeek = dayjs();

    const [index, setIndex] = useState(0);
    const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
    const eventDetails = useRef<ScheduleSpeakingClassesEvent[] | null>(null);
    const [error, setError] = useState<{ invalid: boolean;  message: string | null; }>({
        invalid: false,
        message: null,
    });

    const { events, isLoading: isEventsLoading, pagination, remainingBookings, allowedBookingsPerWeek } = useBookScheduleSpeakingClass(index + 1);
    const { mutate, isLoading: isBooking, isBookingSuccess, setIsBookingSuccess } = useBookScheduleSpeakingClassMutation(() =>
        onConfirm(eventDetails.current),
    );
    
    const datesInWeek = useMemo(
        () =>
        events
            ? Object.keys(events)
                .map((e) => dayjs(e, INTERNAL_DATE_FORMAT))
                .filter((d) => d.isSame(currentWeek.add(index, 'week'), 'week'))
            : [],
        [events, index, currentWeek]
    );
    
    // const handleToggleChange = (value: string[]) => {
    //     if (value.length <= 2) {
    //     setSelectedEvents(value);
    //     } else {
    //     value.splice(1, 1);
    //     }
    // };

    const handleToggleChange = (value: string[]) => {
        const maxClasses = allowedBookingsPerWeek;
        if (value.length <= maxClasses) {
            setSelectedEvents(value);
        } else {
            const errorMessage = t('agenda.max_classes_per_week', { maxClasses });
            setError({
                invalid: true,
                message: errorMessage,
            });
        }
    };
    
    const validateAfterSelect = () => {
        if (selectedEvents.length < 1) {
            const errorMessage = t('agenda.select_classes_to_finish_booking');
            setError({
                invalid: true,
                message: errorMessage,
            });
        return false;
        }
    
        const dates = selectedEvents
        .map((v) => [dayjs(v.split('==')[1], INTERNAL_DATE_FORMAT)])
        .reduceRight((a, b) => (a[0].isSame(b[0], 'week') ? a.concat(b) : a))
        .flat();
    
        if (dates.length > 2) {
            const errorMessage = t('agenda.max_two_classes_per_week');
        setError({
            invalid: true,
            message: errorMessage,
        });
        return false;
        }

        // const topics: Array<string | Dayjs> = selectedEvents.map((v) => [
        //     dayjs(v.split("==")[1], INTERNAL_DATE_FORMAT) ?? "",
        //     events
        //     ? events[v.split("==")[1]][Number(v.split("==")[2])].topic
        //     : "",
        // ]).reduceRight((a, b) =>
        //     (a[0] as Dayjs)?.isSame(b[0], "week") &&
        //     (a[1] as string).includes(b[1] as string)
        //     ? a.concat(b)
        //     : a,
        // );

        // const topics: Array<string | Dayjs> = selectedEvents.map((v) => {
        //     const [dateValue, eventIndexStr] = v.split("==");
        //     const date = dayjs(dateValue, INTERNAL_DATE_FORMAT) ?? "";
        //     const eventIndex = Number(eventIndexStr);
          
        //     // Ensure events and the specific indices are defined
        //     const event = events?.[dateValue]?.[eventIndex];
          
        //     return [date, event ? event.topic : ""];
        //   }).reduceRight((a, b) =>
        //     (a[0] as Dayjs)?.isSame(b[0], "week") &&
        //     (a[1] as string).includes(b[1] as string)
        //       ? a.concat(b)
        //       : a,
        //   );
        
        const topics: Array<string | Dayjs> = selectedEvents.map((v) => {
            const eventDate = v.split("==")[1];
            const eventIndex = Number(v.split("==")[2]);
          
            const event = events?.[eventDate]?.[eventIndex];
          
            return [
              dayjs(eventDate, INTERNAL_DATE_FORMAT) ?? "",
              event?.topic ?? "",
            ];
          }).reduceRight((a, b) => (
            (a[0] as Dayjs)?.isSame(b[0], "week") &&
            (a[1] as string).includes(b[1] as string)
              ? a.concat(b)
              : a
          ), []);
          
    
        if (topics.length > 2) {
            const errorMessage = t('agenda.no_two_same_topic_week');

        setError({
            invalid: true,
            message: errorMessage,
        });
        return false;
        }
    
        setError({ invalid: false, message: null });
        return true;
    };
    
    useEffect(() => {
        if (error.invalid) {
        const timer = setTimeout(() => {
            setError({ invalid: false, message: null });
        }, 5000);
    
        return () => clearTimeout(timer);
        }
    }, [error.invalid]);
      
        // show the start and end time
    const showEventTime = (event: ScheduleSpeakingClassesEvent) => {
        if (event?.ora || event?.oraFine) {
        return `${event?.ora}-${event?.oraFine}`;
        } else {
        const errorMessage = t('agenda.no_event_time_specified');
        return errorMessage
        }
    }

    // show tutor name in the title attribute
    const showTutorName = (event: ScheduleSpeakingClassesEvent) => {
        if (event?.tutors?.first_name || event?.tutors?.last_name) {
        return `${event.tutors?.first_name} ${event.tutors?.last_name.toUpperCase()}`
        }
    }
      
    const onBook = () => {
        if (!me) return;
        if (!validateAfterSelect()) return;
        mutate({
            event_ids: selectedEvents.map((v) => Number(v.split("==")[0])),
            user_token: me.item.user.token,
        });

        if (events) {
            const indices: Array<[number, string]> = selectedEvents.map((k) => [
                Number(k.split("==")[2]),
                k.split("==")[1],
            ]);
            eventDetails.current = indices.map((i) => events[i[1]][i[0]]);
        }
    };
      
    return {
        events,
        isEventsLoading,
        isBooking,
        pagination,
        selectedEvents,
        error,
        datesInWeek,
        currentWeek,
        index,
        isBookingSuccess,
        handleToggleChange,
        validateAfterSelect,
        showEventTime,
        showTutorName,
        onBook,
        setIndex,
        setIsBookingSuccess,
        setSelectedEvents,
        remainingBookings,
        allowedBookingsPerWeek     
    };
};
  
  export default useScheduleSpeakingClass;
  
  